/**
 * The canvas's `settleFrames` (Part 06 Task 6): unhide the frames, open the
 * time page of the first one and fit them on this display once denk mode,
 * native geometry and the mounted camera are ready. Resolves only after the
 * camera acknowledged the fit; rejects with `signal.reason` when aborted.
 */
import type { Viewport } from "@xyflow/react";
import { currentFramePresentationSurface } from "@/desktop/frame-presentation-surface";
import { getViewportInsets } from "@/hooks/use-viewport-insets";
import { ensureCanvasTimePageLayout } from "@/lib/canvas-time-page-layout";
import { getCanvasTimePages } from "@/lib/canvas-time-page-membership";
import { unhideCanvasFrames } from "@/lib/reveal-canvas-frames";
import { useCanvasStore } from "@/stores/canvas-store";
import { useCanvasTimePageStore } from "@/stores/canvas-time-page-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useProjectStore } from "@/stores/project-store";
import { FIT_PADDING_RATIO } from "@/lib/viewport-dimensions";
import { calculateViewportForNodes } from "./viewport-geometry";

const matches = (current: Viewport, expected: Viewport) =>
  Math.abs(current.x - expected.x) < 1 &&
  Math.abs(current.y - expected.y) < 1 &&
  Math.abs(current.zoom - expected.zoom) < 0.001;

export function settleCanvasFrames(
  frameIds: readonly string[],
  signal: AbortSignal,
): Promise<void> {
  if (signal.aborted) return Promise.reject(signal.reason);
  const required = [...frameIds];
  useCanvasStore.getState().setFocusHiddenFrames([]);
  unhideCanvasFrames(required);

  return new Promise((resolve, reject) => {
    let settled = false;
    let fitting = false;
    let expected: Viewport | null = null;
    let renderFrame: number | null = null;
    let settleFrame: number | null = null;
    let unsubscribeCanvas = () => {};
    let unsubscribePresentation = () => {};
    let unsubscribeDesktop = () => {};
    const finish = (failure?: { error: unknown }) => {
      if (settled) return;
      settled = true;
      if (renderFrame !== null) window.cancelAnimationFrame(renderFrame);
      if (settleFrame !== null) window.cancelAnimationFrame(settleFrame);
      signal.removeEventListener("abort", aborted);
      unsubscribeCanvas();
      unsubscribePresentation();
      unsubscribeDesktop();
      if (failure) reject(failure.error);
      else resolve();
    };
    const aborted = () => finish({ error: signal.reason });
    const fail = () =>
      finish({ error: new Error("The workspace frames could not be fitted") });
    const ready = () => {
      const mode = useDesktopMode.getState();
      const canvas = useCanvasStore.getState();
      return (
        mode.mode === "denk" &&
        !mode.nativeGeometryTransitioning &&
        !usePresentationStore.getState().presentation &&
        !!canvas.setViewportProgrammatic &&
        required.every((id) =>
          canvas.nodes.some((node) => node.id === id && !node.hidden),
        )
      );
    };
    const check = () => {
      if (settled) return;
      if (signal.aborted) return aborted();
      unhideCanvasFrames(required);
      if (!ready()) return;
      if (expected) {
        if (
          settleFrame === null &&
          matches(useCanvasStore.getState().viewport, expected)
        )
          settleFrame = window.requestAnimationFrame(() => {
            settleFrame = null;
            if (
              ready() &&
              expected &&
              matches(useCanvasStore.getState().viewport, expected)
            )
              finish();
          });
        return;
      }
      if (fitting) return;
      fitting = true;
      try {
        const spaceId = useProjectStore.getState().currentSpace?.id;
        const nodes = ensureCanvasTimePageLayout({
          nodes: useCanvasStore.getState().nodes,
          spaceId,
        });
        const pages = getCanvasTimePages(nodes, spaceId);
        const pageIndex = pages.findIndex((page) =>
          page.nodeIds.includes(required[0]!),
        );
        if (pageIndex >= 0)
          useCanvasTimePageStore.getState().setActivePageIndex(pageIndex);
        // Let mounted workspace effects reconcile restored Focus dimensions
        // and native geometry before applying the final local display fit.
        renderFrame = window.requestAnimationFrame(() => {
          renderFrame = window.requestAnimationFrame(() => {
            renderFrame = null;
            fitting = false;
            if (settled || signal.aborted) return check();
            if (!ready()) return;
            try {
              const canvas = useCanvasStore.getState();
              const surface = currentFramePresentationSurface(true);
              expected = calculateViewportForNodes(
                canvas.nodes.filter((node) => required.includes(node.id)),
                {
                  viewportWidth: surface.width,
                  viewportHeight: surface.height,
                  insets: getViewportInsets(),
                  paddingRatio: FIT_PADDING_RATIO,
                  maxZoom: 1,
                },
              );
              if (!expected) return fail();
              canvas.setViewportProgrammatic!(expected, { duration: 0 });
              check();
            } catch {
              fail();
            }
          });
        });
      } catch {
        fail();
      }
    };
    unsubscribeCanvas = useCanvasStore.subscribe(check);
    // A presentation ends with its own write, after the canvas restores.
    unsubscribePresentation = usePresentationStore.subscribe(check);
    unsubscribeDesktop = useDesktopMode.subscribe(check);
    signal.addEventListener("abort", aborted, { once: true });
    check();
  });
}
